import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import viewImage from "../../assets/images/icons/view.svg";
import FilterTrigger from "../../commons/FilterTrigger/FilterTrigger";
import FilterOptionsMenu from "../../commons/FilterOptionsMenu/FilterOptionsMenu";
import { Link, useLocation } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import TablePagination from "../../commons/Pagination/TablePagination";
import { ROUTES } from "../../utils/constants";
import { applyListState } from "../../utils/listNavigation";

function CustomerManagement() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [filter, setFilter] = useState("All");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef(null);
  const restoredRef = useRef(false);

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const trimmedSearch = search.trim();
      const queryParams = {
        page,
        limit,
      };

      if (trimmedSearch) {
        queryParams.search = trimmedSearch;
      }

      // Add status filter based on selection
      if (filter === "Active") {
        queryParams.status = "ACTIVE";
      } else if (filter === "Inactive") {
        queryParams.status = "INACTIVE";
      }
      // "All" doesn't send status parameter

      const query = new URLSearchParams(queryParams).toString();

      const response = await handleApiCall("get", `${endpoints.customerList}?${query}`);
      if (response?.data?.code === 200) {
        setData(response?.data?.data?.data || []);
        setTotalPages(response?.data?.data?.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, filter]);

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    const listState = location.state?.listState;
    if (!listState || restoredRef.current) return;
    restoredRef.current = true;
    applyListState(listState, { setPage, setSearch, setFilter });
  }, [location.state]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const toggleStatus = async (status, id) => {
    try {
      const response = await handleApiCall("patch", `${endpoints.customerStatusUpdate}/${id}`, {
        status,
      });
      if (response?.data?.code === 200) {
        toast.success(response?.data?.message || "Customer status updated successfully!");
        getList();
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error updating customer status:", error);
      // Network errors are already handled by handleApiCall
    }
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Customer Management</h3>
            </div>
            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center col-md-12 px-0">
                  <form
                    className="col-md-4 px-0"
                    onSubmit={(e) => {
                      e.preventDefault();
                      getList();
                    }}
                  >
                    <div className="form-group my-0">
                      <input
                        className="form-control search-icon left-icon border"
                        placeholder="Search by name, email id, mobile number"
                        value={search}
                        onChange={handleSearch}
                      />
                    </div>
                  </form>
                  <FilterTrigger
                    ref={filterRef}
                    active={filter !== "All"}
                    className="ms-auto mt-2"
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    {showFilterDropdown && (
                      <FilterOptionsMenu
                        options={["All", "Active", "Inactive"]}
                        value={filter}
                        style={{ right: "40px", top: 0 }}
                        onSelect={(option) => {
                          setFilter(option);
                          setShowFilterDropdown(false);
                          setPage(1);
                        }}
                      />
                    )}
                  </FilterTrigger>
                </div>
              </div>
              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th width="80">S.no.</th>
                        <th width="150">Name</th>
                        <th>Email id</th>
                        <th>Mobile No</th>
                        <th>Status</th>
                        <th>Profile Status</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : data.length > 0 ? (
                        data.map((customer, index) => (
                          <tr key={customer._id || index}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td className="text-center">{customer.full_name || "N/A"}</td>
                            <td>{customer.email || "N/A"}</td>
                            <td>{customer.country_code ? `+${customer.country_code} ${customer.phone}` : customer.phone || "N/A"}</td>
                            <td>
                              <span className={`badge ${customer.status === 1 ? "badge-success" : "badge-danger"}`}>{customer.status === 1 ? "Active" : "Inactive"}</span>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  customer.is_profile_complete === true || customer.is_profile_complete === 1
                                    ? "badge-success"
                                    : "badge-danger"
                                }`}
                              >
                                {customer.is_profile_complete === true || customer.is_profile_complete === 1
                                  ? "Complete"
                                  : "Not Complete"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <Link
                                  to={`/view-customer/${customer._id}`}
                                  state={{
                                    from: ROUTES.CUSTOMER_MANAGEMENT,
                                    listState: { page, search, filter },
                                  }}
                                  className="mx-2"
                                >
                                  <img src={viewImage} alt="View" />
                                </Link>
                                <label className="custom-control custom-switch p-1 ml-4 mb-0 mt-1" role="button">
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    checked={customer.status === 1}
                                    onChange={() => toggleStatus(customer.status === 1 ? 0 : 1, customer._id)}
                                  />
                                  <span className="custom-control-label custom-toggle" />
                                </label>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            No Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerManagement;
